/**
 * Campaign Tags — API client for the provider-agnostic per-campaign Static Tag.
 * GET/PUT /flow-optimizer/campaign-tags/:provider/:campaignId.
 * For Media Devoted | Created By Ciro Autiero
 */

import api from '@/services/api';

import type { CampaignTagProvider } from '@/features/campaigns/tracker/campaign-tracker-access';

export interface CampaignStaticTag {
  id: string;
  name: string;
  color: string;
}

function base(provider: CampaignTagProvider, campaignId: string): string {
  return `/flow-optimizer/campaign-tags/${provider}/${encodeURIComponent(campaignId)}`;
}

// The API wraps payloads in a `{ data }` envelope. A cleared tag is a real
// `null`, so the usual `data.data ?? data` idiom is wrong here — unwrap once.
function unwrap(body: unknown): CampaignStaticTag | null {
  if (body && typeof body === 'object' && 'data' in body) {
    return ((body as { data: CampaignStaticTag | null }).data ?? null);
  }
  return (body as CampaignStaticTag | null) ?? null;
}

export async function getCampaignTag(
  provider: CampaignTagProvider,
  campaignId: string,
): Promise<CampaignStaticTag | null> {
  const { data } = await api.get(base(provider, campaignId));
  return unwrap(data);
}

export async function setCampaignTag(
  provider: CampaignTagProvider,
  campaignId: string,
  staticTagId: string | null,
): Promise<CampaignStaticTag | null> {
  const { data } = await api.put(base(provider, campaignId), { staticTagId });
  return unwrap(data);
}
